import {
  Button,
  darken,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  makeStyles,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useStores } from 'doc-mate-store/lib/hooks';
import { defaultRootStore } from 'doc-mate-store/lib/models/Store';
import * as api from 'doc-mate-store/lib/services/api';
import uniqBy from 'lodash/uniqBy';
import React, { useCallback, useMemo, useState } from 'react';
import { colors } from '../../../themes';
import DriverAssignConfirmation from './DriverAssignConfirmation';
import { DriverDetails, DriverOption } from './types';

type Props = {
  driverName: string;
  driverId: string;
  loadId: number;
  isDispatcher: boolean;
  linkedLegs: number[];
};

const LoadDriverCellFields: React.FC<Props> = props => {
  const { driverName, driverId, loadId, isDispatcher, linkedLegs } = props;

  const [error, setError] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState<boolean>(false); //eslint-disable-line
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [newDriver, setNewDriver] = useState<DriverOption | null>(null);
  const [driverDetails, setDriverDetails] = useState<DriverDetails | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Missing truck modal state
  const [noTruckDialogOpen, setNoTruckDialogOpen] = useState<boolean>(false);
  const [noTruckMessage, setNoTruckMessage] = useState<string>('');

  const { rootStore } = useStores();
  const classes = useStyles();

  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    setIsHovering(false);
    event.stopPropagation();
  }, []);

  const loadDriverInfo = useCallback(async (driverId: string) => {
    const { token } = defaultRootStore;
    let entities: DriverDetails | undefined = undefined;
    if (!driverId) {
      setDriverDetails(null);
      return;
    }
    try {
      ({
        response: { entities },
      } = (await api.fetchDriverInfo(token!, driverId)) as any);
    } catch (error) {
      console.log('[DEBUG] error', (error as Error).message);
    }

    if (entities) {
      setDriverDetails(entities);
    } else {
      setDriverDetails(null);
    }
  }, []);

  const handleDriverError = useCallback((errorMsg: string) => {
    if (errorMsg.toLowerCase().includes('truck')) {
      setNoTruckMessage(errorMsg);
      setNoTruckDialogOpen(true);
    } else {
      setError(errorMsg);
    }
  }, []);

  // Always require confirmation before assigning, replacing, or unassigning a driver
  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, value: DriverOption | null) => {
      event.stopPropagation();
      setError(null);
      if (value) {
        const newValue = value.value as string;
        if (newValue !== driverId) {
          setOpenConfirm(true);
          setNewDriver(value);
        }
      }
    },
    [driverId],
  );

  const { users: users_ } = rootStore;
  const users = Array.from(users_.values()).filter(user => user.isDriver);

  const drivers = useMemo(() => {
    let options: DriverOption[] = users.map(user => ({
      name: `${user.lastName}, ${user.firstName}`,
      value: user.getRefId(),
      organization: user.organization?.getRefId(),
      organizationName: user.organizationName,
    }));

    options = options.concat({
      name: 'No driver assigned',
      value: '',
      organizationName: '',
    });
    options.sort((a, b) => {
      if (a.name === 'No driver assigned') return -1;
      const organizationSorter = a.organizationName
        .toLowerCase()
        .localeCompare(b.organizationName.toLowerCase());
      const nameSorter = a.name
        .toLowerCase()
        .localeCompare(b.name.toLowerCase());
      return organizationSorter || nameSorter;
    });
    // @ts-ignore
    return uniqBy(options, v => [v.name, v.organizationName].join());
  }, [users]);

  const handleGetOptionSelected = useCallback(
    (option: DriverOption, selected: DriverOption) =>
      option.value === selected.value,
    [],
  );

  const handleConfirm = useCallback(async () => {
    const load = rootStore.loadTableRows.get(`${loadId}`);
    setIsLoading(true);
    if (load && newDriver) {
      const response = await load.assignDriver(newDriver.value);
      if (newDriver.value) {
        await loadDriverInfo(newDriver.value);
      }
      if (response && !response.ok) {
        if (
          response.errors &&
          response.errors.driver &&
          response.errors.driver.length > 0
        ) {
          handleDriverError(response.errors.driver[0]);
        }
      }
      setIsOpen(false);
      setOpenConfirm(false);
      setIsLoading(false);
      setNewDriver(null);
    }
  }, [
    handleDriverError,
    loadDriverInfo,
    loadId,
    newDriver,
    rootStore.loadTableRows,
  ]);

  const handleCancel = useCallback(async () => {
    setOpenConfirm(false);
    setIsOpen(false);
    setNewDriver(null);
  }, []);

  const handleMouseEnter = useCallback(async () => {
    if (driverId && !driverDetails) {
      loadDriverInfo(driverId);
    }
    if (!isOpen && !!driverId) {
      setIsHovering(true);
    }
  }, [driverDetails, driverId, isOpen, loadDriverInfo]);

  const handleMouseLeave = useCallback(() => {
    if (!isOpen) {
      setIsHovering(false);
    }
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setIsHovering(false);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsHovering(false);
  }, []);

  const getOptionLabel = useCallback(
    (option: { name: any }) => option.name,
    [],
  );
  const renderInput = useCallback(
    (params: JSX.IntrinsicAttributes & TextFieldProps) => (
      <TextField {...params} className={classes.select} />
    ),
    [classes.select],
  );
  const driverValue = useMemo(
    () => ({
      name: driverName,
      value: driverId,
      organizationName: '',
    }),
    [driverId, driverName],
  );
  const popUpIcon = useMemo(
    () => (isDispatcher ? <ArrowDropDownIcon /> : null),
    [isDispatcher],
  );

  return (
    <>
      <FormControl
        error={!!error}
        fullWidth
        onMouseOut={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
      >
        <Autocomplete
          className={classes.dropdown}
          fullWidth
          getOptionSelected={handleGetOptionSelected}
          // @ts-ignore
          options={drivers}
          getOptionLabel={getOptionLabel}
          renderInput={renderInput}
          selectOnFocus
          blurOnSelect
          disableClearable
          disabled={!isDispatcher}
          popupIcon={popUpIcon}
          onChange={handleChange}
          onOpen={handleOpen}
          onClose={handleClose}
          onClick={handleClick}
          value={driverValue}
          groupBy={options => options.organizationName}
          filterOptions={(options, { inputValue }) =>
            options.filter(
              item =>
                item.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                (item.organizationName &&
                  item.organizationName
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())),
            )
          }
        />
        {error && (
          <FormHelperText className={classes.error}>{error}</FormHelperText>
        )}

        {/* Driver assign / unassign / replacement confirmation modal */}
        {!isOpen && openConfirm && newDriver && (
          <DriverAssignConfirmation
            loading={isLoading}
            legId={loadId}
            driver={newDriver.name}
            legInfoList={linkedLegs}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        )}

        {/* Missing Truck Modal */}
        <Dialog
          open={noTruckDialogOpen}
          onClose={() => setNoTruckDialogOpen(false)}
          fullWidth
          maxWidth="xs"
          className={classes.dialogModal}
        >
          <DialogTitle disableTypography className={classes.headerModal}>
            <span>Truck Required</span>
          </DialogTitle>
          <Divider />
          <DialogContent className={classes.mainModal}>
            <div className="dialog-text">
              {noTruckMessage ||
                'Please assign a truck for this driver in the AS400 first.'}
            </div>
            <div className={classes.actionsModal}>
              <Button
                className={classes.okBtn}
                onClick={() => setNoTruckDialogOpen(false)}
                disableFocusRipple
                disableTouchRipple
              >
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </FormControl>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  select: {
    backgroundColor: 'transparent',
    '&&&& input': {
      paddingLeft: 5,
    },
    '& .MuiInput-underline:hover:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
  },
  dropdown: {
    '& .Mui-focused': {
      border: '1px solid rgba(0, 0, 0, 0.87)',
      backgroundColor: 'white',
      borderRadius: 4,
    },
  },
  driverInfo: {
    width: 450,
    backgroundColor: '#155878',
    position: 'absolute',
    left: 0,
    marginLeft: '75%',
    color: 'white',
    padding: '29px 30px',
    borderRadius: 5,
    'z-index': 10,
    '& img': {
      height: 50,
      width: 50,
      borderRadius: 25,
    },
  },
  error: {
    whiteSpace: 'pre-wrap',
  },
  popUpText: {
    fontSize: 12,
    padding: 5,
  },
  content: {
    paddingTop: 10,
    paddingBottom: 30,
  },
  dialogModal: {
    '& .MuiDialog-paperWidthXs': {
      maxWidth: 400,
    },
  },
  headerModal: {
    fontSize: 18,
    color: '#4f4f4f',
    fontWeight: 700,
    textAlign: 'center',
    padding: theme.spacing(1.5),
    textTransform: 'uppercase',
  },
  mainModal: {
    padding: theme.spacing(1, 2),
    '& .dialog-text': {
      textAlign: 'center',
      fontSize: 15,
      padding: theme.spacing(1, 0),
      color: '#4f4f4f',
    },
  },
  actionsModal: {
    alignItems: 'center',
    borderTop: '1px solid #bdbdbd',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
  },
  okBtn: {
    backgroundColor: colors.textPrimary[400],
    color: 'white',
    borderRadius: 5,
    padding: theme.spacing(0.8, 4),
    '&:hover': {
      backgroundColor: darken(colors.textPrimary[400], 0.15),
    },
  },
}));

export default React.memo(LoadDriverCellFields);
